import { Html } from '@react-three/drei';
import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import type CameraControlsImpl from 'camera-controls';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

import Text from '@cartwhl/ui/base/Text';
import LockIcon from '@cartwhl/ui/icons/LockIcon';
import PauseIcon from '@cartwhl/ui/icons/PauseIcon';
import PlayIcon from '@cartwhl/ui/icons/PlayIcon';

import type { GizmoRotationAxis } from '../../../../../cartographerStore';
import {
  readBonePoseBaselineTransform,
  readBonePoseTransform,
} from '../../../../../lib/skeleton/boneTransforms';
import { RotationLimits, type Skeleton } from '../../../../../lib/skeleton/core';
import {
  buildGimbalQuaternions,
  computeGimbalAngleOffsets,
  decomposeMatrixToTransform,
} from '../../../../../lib/skeleton/rotationService';
import {
  ensureRotationLimits,
  getRotationLimitsAxisMode,
  pruneBonePropertiesDefaults,
  setRotationLimitsAxisMode,
} from '../../../../../lib/skeleton/rotationLimits';
import {
  ACTIVE_RING_OPACITY,
  AXIS_COLOR_BY_KEY,
  AXIS_ORDER,
  HANDLE_ANGLE_DEGREES,
  HANDLE_CORNER_RADIUS_FACTOR,
  HANDLE_PICK_SCALE,
  HANDLE_RADIAL_LIFT,
  HANDLE_WIDTH_FACTOR,
  MIN_LIMIT_RANGE_DEGREES,
  SWEEP_FILL_OPACITY,
  IDLE_RING_OPACITY,
  LOCK_PILL_TOP_RIGHT_FACTOR,
  LOCKED_RING_COLOR,
  LOCKED_RING_OPACITY,
  LOCKED_RING_RIBBON_HALF_WIDTH,
  PICK_EDGE_ON_THRESHOLD,
  PICK_RADIAL_TOLERANCE,
  PREVIEW_APPLY_EPSILON_DEGREES,
  PREVIEW_DEGREES_PER_SECOND,
  PREVIEW_MIN_RANGE_DEGREES,
  PREVIEW_MIN_SEGMENT_DURATION_MS,
  RING_RADIUS,
  RING_RIBBON_HALF_WIDTH,
  RING_SEGMENT_COUNT,
} from '../constants';
import type {
  AxisLimitsState,
  LimitBoundary,
  ManipulatorAxis,
} from '../types';
import {
  angleDegreesFromLocalPoint,
  axisIndexFromKey,
  axisNormalVector,
  axisPointFromDegrees,
  createAxisRibbonEdgeGeometry,
  createAxisRibbonGeometry,
  createAxisSweepGeometry,
  isAngleInArc,
  normalizeDegrees,
  resolveDisplayBounds,
} from '../utils';
import {
  clampManipulatorScale,
  computeTransformControlsScale,
} from '../../shared';

interface RotationLimitsManipulatorProps {
  skeleton: Skeleton;
  selectedBoneIndex: number | null;
  enabled: boolean;
  manipulatorScale: number;
  poseVersion: number;
  propertiesVersion: number;
  onBonePropertiesChange?: () => void;
  onApplyBoneRotateFromGimbalDelta?: (
    boneIndex: number,
    axis: GizmoRotationAxis,
    deltaDegrees: number,
  ) => void;
  onSetBoneEulerRotation?: (
    boneIndex: number,
    eulerDegrees: [number, number, number],
  ) => void;
  onDraggingChange?: (isDragging: boolean) => void;
}

interface RotationLimitsAxisProps {
  axisState: AxisLimitsState;
  isActive: boolean;
  axisQuaternion: THREE.Quaternion;
  angleOffset: number;
  manipulatorStateRef: React.RefObject<ManipulatorFrameState>;
  onSelectAxis: (axis: ManipulatorAxis) => void;
  onHandlePointerDown: (
    axis: ManipulatorAxis,
    boundary: LimitBoundary,
    event: ThreeEvent<PointerEvent>,
  ) => void;
}

interface DragState {
  axis: ManipulatorAxis;
  boundary: LimitBoundary;
}

interface DragAnchor {
  initialValue: number;
  otherBoundary: number;
  lastAngle: number;
  accumulatedDelta: number;
  wasUnlimited: boolean;
}

interface ManipulatorFrameState {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  scale: number;
}

type PreviewPlaybackState = 'stopped' | 'playing' | 'paused';

interface PreviewSegment {
  boneIndex: number;
  axis: ManipulatorAxis;
  min: number;
  max: number;
  from: number;
  to: number;
  startMs: number;
  durationMs: number;
}

interface RibbonSquareHandleProps {
  position: THREE.Vector3;
  normal: THREE.Vector3;
  radial: THREE.Vector3;
  direction: 1 | -1;
  halfSize: number;
  color: number;
  opacity: number;
  edgeOpacity: number;
  onPointerDown?: (event: ThreeEvent<PointerEvent>) => void;
}

const UNLIMITED_SWEEP_GAP_DEGREES = 0;
const MAX_AXIS_CLICK_TRAVEL_PX = 6;
const HANDLE_CORNER_SEGMENTS = 4;

function easeInOutSine(value: number): number {
  return -(Math.cos(Math.PI * value) - 1) / 2;
}

function resolvePreviewDurationMs(fromDegrees: number, toDegrees: number): number {
  const travelDegrees = Math.abs(toDegrees - fromDegrees);
  const durationMs = (travelDegrees / PREVIEW_DEGREES_PER_SECOND) * 1000;
  return Math.max(PREVIEW_MIN_SEGMENT_DURATION_MS, durationMs);
}

function buildRoundedHandlePerimeter(
  halfWidth: number,
  height: number,
  cornerRadius: number,
): [number, number][] {
  const cr = Math.min(cornerRadius, halfWidth, height / 2);
  const pts: [number, number][] = [];

  pts.push([-halfWidth, 0]);
  pts.push([-halfWidth, height - cr]);
  for (let i = 1; i <= HANDLE_CORNER_SEGMENTS; i++) {
    const a = Math.PI - (Math.PI / 2) * (i / HANDLE_CORNER_SEGMENTS);
    pts.push([-halfWidth + cr + Math.cos(a) * cr, height - cr + Math.sin(a) * cr]);
  }
  pts.push([halfWidth - cr, height]);
  for (let i = 1; i <= HANDLE_CORNER_SEGMENTS; i++) {
    const a = Math.PI / 2 - (Math.PI / 2) * (i / HANDLE_CORNER_SEGMENTS);
    pts.push([halfWidth - cr + Math.cos(a) * cr, height - cr + Math.sin(a) * cr]);
  }
  pts.push([halfWidth, 0]);

  return pts;
}

function perimeterTo3DPositions(
  perimeter: [number, number][],
  normalDir: THREE.Vector3,
  radialDir: THREE.Vector3,
  liftVec: THREE.Vector3,
): number[] {
  const positions: number[] = [];
  for (const [nOff, rOff] of perimeter) {
    positions.push(
      normalDir.x * nOff + radialDir.x * rOff + liftVec.x,
      normalDir.y * nOff + radialDir.y * rOff + liftVec.y,
      normalDir.z * nOff + radialDir.z * rOff + liftVec.z,
    );
  }
  return positions;
}

function RibbonSquareHandle({
  position,
  normal,
  radial,
  direction,
  halfSize,
  color,
  opacity,
  edgeOpacity,
  onPointerDown,
}: RibbonSquareHandleProps) {
  const pickHalfSize = halfSize * HANDLE_PICK_SCALE;

  const extensionDir = useMemo(() => {
    const n = normal.clone().normalize();
    const rad = radial.clone().normalize();
    const tang = new THREE.Vector3().crossVectors(n, rad).normalize();
    const a = HANDLE_ANGLE_DEGREES * THREE.MathUtils.DEG2RAD;
    return new THREE.Vector3()
      .addScaledVector(tang, direction * Math.cos(a))
      .addScaledVector(rad, Math.sin(a))
      .normalize();
  }, [direction, normal, radial]);

  const squareGeometry = useMemo(() => {
    const n = normal.clone().normalize();
    const rad = radial.clone().normalize();
    const lift = rad.clone().multiplyScalar(HANDLE_RADIAL_LIFT);
    const handleHeight = halfSize * 2;
    const cr = halfSize * HANDLE_CORNER_RADIUS_FACTOR;

    const perimeter = buildRoundedHandlePerimeter(halfSize, handleHeight, cr);
    const positions = perimeterTo3DPositions(perimeter, n, extensionDir, lift);

    const indices: number[] = [];
    for (let i = 1; i < perimeter.length - 1; i++) {
      indices.push(0, i, i + 1);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }, [extensionDir, halfSize, normal, radial]);

  const pickGeometry = useMemo(() => {
    const n = normal.clone().normalize();
    const rad = radial.clone().normalize();
    const lift = rad.clone().multiplyScalar(HANDLE_RADIAL_LIFT * 1.2);
    const pickHeight = pickHalfSize * 2;
    const cr = pickHalfSize * HANDLE_CORNER_RADIUS_FACTOR;

    const perimeter = buildRoundedHandlePerimeter(pickHalfSize, pickHeight, cr);
    const positions = perimeterTo3DPositions(perimeter, n, extensionDir, lift);

    const indices: number[] = [];
    for (let i = 1; i < perimeter.length - 1; i++) {
      indices.push(0, i, i + 1);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }, [extensionDir, halfSize, pickHalfSize, normal, radial]);

  const edgeGeometry = useMemo(() => {
    const n = normal.clone().normalize();
    const rad = radial.clone().normalize();
    const lift = rad.clone().multiplyScalar(HANDLE_RADIAL_LIFT * 1.1);
    const handleHeight = halfSize * 2;
    const cr = halfSize * HANDLE_CORNER_RADIUS_FACTOR;

    const perimeter = buildRoundedHandlePerimeter(halfSize, handleHeight, cr);
    const ext = extensionDir;
    const edgePositions: number[] = [];
    for (let i = 0; i < perimeter.length; i++) {
      const [nx1, ry1] = perimeter[i];
      const [nx2, ry2] = perimeter[(i + 1) % perimeter.length];
      edgePositions.push(
        n.x * nx1 + ext.x * ry1 + lift.x, n.y * nx1 + ext.y * ry1 + lift.y, n.z * nx1 + ext.z * ry1 + lift.z,
        n.x * nx2 + ext.x * ry2 + lift.x, n.y * nx2 + ext.y * ry2 + lift.y, n.z * nx2 + ext.z * ry2 + lift.z,
      );
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(edgePositions, 3));
    return geometry;
  }, [extensionDir, halfSize, normal, radial]);

  useEffect(
    () => () => {
      squareGeometry.dispose();
    },
    [squareGeometry],
  );
  useEffect(
    () => () => {
      pickGeometry.dispose();
    },
    [pickGeometry],
  );
  useEffect(
    () => () => {
      edgeGeometry.dispose();
    },
    [edgeGeometry],
  );

  const pickRaycast = useMemo(() => {
    return function raycast(
      this: THREE.Mesh,
      raycaster: THREE.Raycaster,
      intersects: THREE.Intersection[],
    ) {
      const start = intersects.length;
      THREE.Mesh.prototype.raycast.call(this, raycaster, intersects);
      for (let i = start; i < intersects.length; i++) {
        intersects[i].distance *= 0.99;
      }
    };
  }, []);

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (!onPointerDown) return;
    event.stopPropagation();
    onPointerDown(event);
  };

  return (
    <group position={position} renderOrder={1250}>
      {onPointerDown && (
        <mesh geometry={pickGeometry} raycast={pickRaycast} onPointerDown={handlePointerDown}>
          <meshBasicMaterial
            transparent
            opacity={0}
            depthTest={false}
            depthWrite={false}
            colorWrite={false}
            toneMapped={false}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
      <mesh geometry={squareGeometry}>
        <meshBasicMaterial
          color={color}
          transparent
          opacity={opacity}
          side={THREE.DoubleSide}
          depthTest={false}
          toneMapped={false}
        />
      </mesh>
      <lineSegments geometry={edgeGeometry}>
        <lineBasicMaterial
          color={color}
          transparent
          opacity={edgeOpacity}
          depthTest={false}
          toneMapped={false}
        />
      </lineSegments>
    </group>
  );
}

function RotationLimitsAxis({
  axisState,
  isActive,
  axisQuaternion,
  angleOffset,
  manipulatorStateRef,
  onSelectAxis,
  onHandlePointerDown,
}: RotationLimitsAxisProps) {
  const axisQuaternionRef = useRef(axisQuaternion.clone());
  axisQuaternionRef.current.copy(axisQuaternion);

  const axisQuaternionTuple = useMemo<[number, number, number, number]>(
    () => [axisQuaternion.x, axisQuaternion.y, axisQuaternion.z, axisQuaternion.w],
    [axisQuaternion],
  );
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null);
  const arcRef = useRef({ startDegrees: -180, endDegrees: 180 });
  const axis = axisState.axis;

  const displayBounds = resolveDisplayBounds(
    axisState.mode,
    axisState.min,
    axisState.max,
  );
  const isLocked = axisState.mode === 'locked';
  const showLimitedSweep = axisState.mode === 'limited';
  const ringStartDegrees =
    (showLimitedSweep
      ? displayBounds.min
      : -180 + UNLIMITED_SWEEP_GAP_DEGREES / 2) + angleOffset;
  const ringEndDegrees =
    (showLimitedSweep
      ? displayBounds.max
      : 180 - UNLIMITED_SWEEP_GAP_DEGREES / 2) + angleOffset;

  arcRef.current.startDegrees = ringStartDegrees;
  arcRef.current.endDegrees = ringEndDegrees;

  const ringHalfWidth = isLocked
    ? LOCKED_RING_RIBBON_HALF_WIDTH
    : RING_RIBBON_HALF_WIDTH;

  const ringGeometry = useMemo(
    () =>
      createAxisRibbonGeometry({
        axis: axisState.axis,
        startDegrees: ringStartDegrees,
        endDegrees: ringEndDegrees,
        radius: RING_RADIUS,
        halfWidth: ringHalfWidth,
        segmentCount: RING_SEGMENT_COUNT,
      }),
    [axisState.axis, ringEndDegrees, ringHalfWidth, ringStartDegrees],
  );
  const ringOuterEdgeGeometry = useMemo(
    () =>
      createAxisRibbonEdgeGeometry({
        axis: axisState.axis,
        startDegrees: ringStartDegrees,
        endDegrees: ringEndDegrees,
        radius: RING_RADIUS,
        normalOffset: ringHalfWidth,
        segmentCount: RING_SEGMENT_COUNT,
      }),
    [axisState.axis, ringEndDegrees, ringHalfWidth, ringStartDegrees],
  );
  const ringInnerEdgeGeometry = useMemo(
    () =>
      createAxisRibbonEdgeGeometry({
        axis: axisState.axis,
        startDegrees: ringStartDegrees,
        endDegrees: ringEndDegrees,
        radius: RING_RADIUS,
        normalOffset: -ringHalfWidth,
        segmentCount: RING_SEGMENT_COUNT,
      }),
    [axisState.axis, ringEndDegrees, ringHalfWidth, ringStartDegrees],
  );

  const sweepFillGeometry = useMemo(
    () =>
      showLimitedSweep
        ? createAxisSweepGeometry({
            axis: axisState.axis,
            startDegrees: ringStartDegrees,
            endDegrees: ringEndDegrees,
            radius: RING_RADIUS,
            segmentCount: RING_SEGMENT_COUNT,
          })
        : null,
    [axisState.axis, ringEndDegrees, ringStartDegrees, showLimitedSweep],
  );

  useEffect(
    () => () => {
      ringGeometry.dispose();
    },
    [ringGeometry],
  );
  useEffect(
    () => () => {
      ringOuterEdgeGeometry.dispose();
    },
    [ringOuterEdgeGeometry],
  );
  useEffect(
    () => () => {
      ringInnerEdgeGeometry.dispose();
    },
    [ringInnerEdgeGeometry],
  );
  useEffect(
    () => () => {
      sweepFillGeometry?.dispose();
    },
    [sweepFillGeometry],
  );

  const ribbonRaycast = useMemo(() => {
    const plane = new THREE.Plane();
    const hitPoint = new THREE.Vector3();
    const localPoint = new THREE.Vector3();
    const normalVec = new THREE.Vector3();
    const invQuat = new THREE.Quaternion();

    return function raycast(
      this: THREE.Mesh,
      raycaster: THREE.Raycaster,
      intersects: THREE.Intersection[],
    ) {
      const ms = manipulatorStateRef.current;
      if (!ms) return;

      const aq = axisQuaternionRef.current;

      normalVec
        .copy(axisNormalVector(axis))
        .applyQuaternion(aq)
        .normalize();

      const dir = raycaster.ray.direction;
      const viewDot = Math.abs(
        dir.x * normalVec.x + dir.y * normalVec.y + dir.z * normalVec.z,
      );
      if (viewDot > PICK_EDGE_ON_THRESHOLD) return;

      plane.setFromNormalAndCoplanarPoint(normalVec, ms.position);
      if (!raycaster.ray.intersectPlane(plane, hitPoint)) return;

      invQuat.copy(aq).invert();
      localPoint
        .copy(hitPoint)
        .sub(ms.position)
        .applyQuaternion(invQuat)
        .divideScalar(ms.scale);

      const radialDist = localPoint.length();
      if (Math.abs(radialDist - RING_RADIUS) > PICK_RADIAL_TOLERANCE) return;

      const angle = angleDegreesFromLocalPoint(axis, localPoint);
      const arc = arcRef.current;
      if (!isAngleInArc(angle, arc.startDegrees, arc.endDegrees)) return;

      const distance = hitPoint.distanceTo(raycaster.ray.origin);
      intersects.push({
        distance,
        point: hitPoint.clone(),
        object: this,
      } as THREE.Intersection);
    };
  }, [axis, manipulatorStateRef]);

  const ringNormal = useMemo(
    () => axisNormalVector(axisState.axis),
    [axisState.axis],
  );
  const ringColor = isLocked ? LOCKED_RING_COLOR : AXIS_COLOR_BY_KEY[axisState.axis];
  const ribbonHighlights = isActive && axisState.mode !== 'limited';
  const ringOpacity = isLocked
    ? (ribbonHighlights ? ACTIVE_RING_OPACITY : LOCKED_RING_OPACITY)
    : (ribbonHighlights ? ACTIVE_RING_OPACITY : IDLE_RING_OPACITY);
  const ringEdgeOpacity = Math.min(1, ringOpacity + 0.2);
  const handleHighlights = isActive && axisState.mode === 'limited';
  const handleOpacity = handleHighlights ? ACTIVE_RING_OPACITY : IDLE_RING_OPACITY;
  const handleEdgeOpacity = Math.min(1, handleOpacity + 0.2);
  const handleHalfSize = ringHalfWidth * HANDLE_WIDTH_FACTOR;

  const startHandlePosition = useMemo(
    () => axisPointFromDegrees(axisState.axis, ringStartDegrees, RING_RADIUS),
    [axisState.axis, ringStartDegrees],
  );
  const endHandlePosition = useMemo(
    () => axisPointFromDegrees(axisState.axis, ringEndDegrees, RING_RADIUS),
    [axisState.axis, ringEndDegrees],
  );
  const startHandleRadial = useMemo(
    () => axisPointFromDegrees(axisState.axis, ringStartDegrees, 1).normalize(),
    [axisState.axis, ringStartDegrees],
  );
  const endHandleRadial = useMemo(
    () => axisPointFromDegrees(axisState.axis, ringEndDegrees, 1).normalize(),
    [axisState.axis, ringEndDegrees],
  );

  const handleSelectAxisPointerDown = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    if (event.nativeEvent.button !== 0) return;
    pointerDownRef.current = {
      x: event.nativeEvent.clientX,
      y: event.nativeEvent.clientY,
    };
  };
  const handleSelectAxisClick = (event: ThreeEvent<MouseEvent>) => {
    const startPoint = pointerDownRef.current;
    pointerDownRef.current = null;
    if (startPoint) {
      const dx = event.nativeEvent.clientX - startPoint.x;
      const dy = event.nativeEvent.clientY - startPoint.y;
      if (Math.hypot(dx, dy) > MAX_AXIS_CLICK_TRAVEL_PX) {
        event.stopPropagation();
        return;
      }
    }
    event.stopPropagation();
    onSelectAxis(axisState.axis);
  };
  const handleStartHandlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    onHandlePointerDown(axisState.axis, 'min', event);
  };
  const handleEndHandlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    onHandlePointerDown(axisState.axis, 'max', event);
  };

  return (
    <group quaternion={axisQuaternionTuple}>
      <mesh
        geometry={ringGeometry}
        raycast={ribbonRaycast}
        onPointerDown={handleSelectAxisPointerDown}
        onClick={handleSelectAxisClick}
      >
        <meshBasicMaterial
          color={ringColor}
          transparent
          opacity={ringOpacity}
          side={THREE.DoubleSide}
          depthTest={false}
          toneMapped={false}
        />
      </mesh>
      <lineSegments geometry={ringOuterEdgeGeometry}>
        <lineBasicMaterial
          color={ringColor}
          transparent
          opacity={ringEdgeOpacity}
          depthTest={false}
          toneMapped={false}
        />
      </lineSegments>
      <lineSegments geometry={ringInnerEdgeGeometry}>
        <lineBasicMaterial
          color={ringColor}
          transparent
          opacity={ringEdgeOpacity}
          depthTest={false}
          toneMapped={false}
        />
      </lineSegments>
      {sweepFillGeometry && (
        <mesh geometry={sweepFillGeometry}>
          <meshBasicMaterial
            color={AXIS_COLOR_BY_KEY[axisState.axis]}
            transparent
            opacity={SWEEP_FILL_OPACITY}
            side={THREE.DoubleSide}
            depthTest={false}
            toneMapped={false}
          />
        </mesh>
      )}
      {axisState.mode === 'limited' && (
        <>
          <RibbonSquareHandle
            position={startHandlePosition}
            normal={ringNormal}
            radial={startHandleRadial}
            direction={-1}
            halfSize={handleHalfSize}
            color={AXIS_COLOR_BY_KEY[axisState.axis]}
            opacity={handleOpacity}
            edgeOpacity={handleEdgeOpacity}
            onPointerDown={handleStartHandlePointerDown}
          />
          <RibbonSquareHandle
            position={endHandlePosition}
            normal={ringNormal}
            radial={endHandleRadial}
            direction={1}
            halfSize={handleHalfSize}
            color={AXIS_COLOR_BY_KEY[axisState.axis]}
            opacity={handleOpacity}
            edgeOpacity={handleEdgeOpacity}
            onPointerDown={handleEndHandlePointerDown}
          />
        </>
      )}
    </group>
  );
}

export default function RotationLimitsManipulator({
  skeleton,
  selectedBoneIndex,
  enabled,
  manipulatorScale,
  poseVersion,
  propertiesVersion,
  onBonePropertiesChange,
  onApplyBoneRotateFromGimbalDelta,
  onSetBoneEulerRotation,
  onDraggingChange,
}: RotationLimitsManipulatorProps) {
  const cameraControls = useThree(
    (state) => state.controls,
  ) as CameraControlsImpl | null;
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);
  const rootRef = useRef<THREE.Group>(null);
  const lockPillAnchorRef = useRef<THREE.Group>(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const planeRef = useRef(new THREE.Plane());
  const intersectionRef = useRef(new THREE.Vector3());
  const localPointRef = useRef(new THREE.Vector3());
  const axisNormalRef = useRef(new THREE.Vector3());
  const anchorPositionRef = useRef(new THREE.Vector3());
  const anchorQuaternionRef = useRef(new THREE.Quaternion());
  const anchorScaleRef = useRef(new THREE.Vector3());
  const cameraRightRef = useRef(new THREE.Vector3());
  const cameraUpRef = useRef(new THREE.Vector3());
  const cameraDiagonalRef = useRef(new THREE.Vector3());
  const lockPillWorldPositionRef = useRef(new THREE.Vector3());
  const ndcRef = useRef(new THREE.Vector2());
  const dragAnchorRef = useRef<DragAnchor | null>(null);

  const manipulatorStateRef = useRef<ManipulatorFrameState>({
    position: new THREE.Vector3(),
    quaternion: new THREE.Quaternion(),
    scale: 1,
  });

  const [activeAxis, setActiveAxis] = useState<ManipulatorAxis>('x');
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [previewPlaybackState, setPreviewPlaybackState] =
    useState<PreviewPlaybackState>('stopped');
  const previewRafIdRef = useRef<number | null>(null);
  const previewSegmentRef = useRef<PreviewSegment | null>(null);
  const previewEulerSnapshotRef = useRef<[number, number, number] | null>(null);
  const setEulerRotationRef = useRef(onSetBoneEulerRotation);
  setEulerRotationRef.current = onSetBoneEulerRotation;
  const skeletonRef = useRef(skeleton);
  skeletonRef.current = skeleton;

  const clearPreviewAnimationFrame = useCallback(() => {
    if (previewRafIdRef.current === null) return;
    window.cancelAnimationFrame(previewRafIdRef.current);
    previewRafIdRef.current = null;
  }, []);

  const stopPreviewPlayback = useCallback(
    (nextState: PreviewPlaybackState = 'stopped') => {
      clearPreviewAnimationFrame();
      if (nextState === 'stopped') {
        previewSegmentRef.current = null;
      }
      previewEulerSnapshotRef.current = null;
      setPreviewPlaybackState(nextState);
    },
    [clearPreviewAnimationFrame],
  );

  const selectedBone = useMemo(() => {
    if (!enabled) return null;
    if (selectedBoneIndex === null) return null;
    if (selectedBoneIndex < 0 || selectedBoneIndex >= skeleton.bones.length) {
      return null;
    }
    return skeleton.bone(selectedBoneIndex);
  }, [enabled, selectedBoneIndex, skeleton]);

  const selectedBoneIdentity =
    selectedBone === null ? null : `${selectedBone.index}:${selectedBone.name}`;

  useEffect(() => {
    const segment = previewSegmentRef.current;
    stopPreviewPlayback('stopped');

    if (segment && setEulerRotationRef.current) {
      const prevBone = skeletonRef.current.bone(segment.boneIndex);
      const baseline = readBonePoseBaselineTransform(prevBone).rotate;
      setEulerRotationRef.current(segment.boneIndex, [
        baseline[0],
        baseline[1],
        baseline[2],
      ]);
    }

    setActiveAxis('x');
    setDragState(null);
    dragAnchorRef.current = null;
  }, [selectedBoneIdentity, stopPreviewPlayback]);

  const axisStates = useMemo<AxisLimitsState[]>(() => {
    if (!selectedBone) return [];
    const limits = selectedBone.properties?.rotationLimits ?? new RotationLimits();
    return AXIS_ORDER.map((axis) => {
      const axisIndex = axisIndexFromKey(axis);
      return {
        axis,
        axisIndex,
        mode: getRotationLimitsAxisMode(limits, axisIndex),
        min: limits.min[axisIndex],
        max: limits.max[axisIndex],
      };
    });
  }, [propertiesVersion, selectedBone]);

  const activeAxisState =
    axisStates.find((state) => state.axis === activeAxis) ?? null;
  const activeAxisMode = activeAxisState?.mode ?? 'unlimited';
  const activeAxisLabel = activeAxis.toUpperCase();
  const activeAxisColorCss = useMemo(
    () => new THREE.Color(AXIS_COLOR_BY_KEY[activeAxis]).getStyle(),
    [activeAxis],
  );
  const activeAxisLimitedBounds = useMemo(() => {
    if (!activeAxisState || activeAxisState.mode !== 'limited') return null;
    return resolveDisplayBounds(
      'limited',
      activeAxisState.min,
      activeAxisState.max,
    );
  }, [activeAxisState]);
  const activeAxisLimitedRangeDegrees =
    activeAxisLimitedBounds === null
      ? 0
      : Math.abs(activeAxisLimitedBounds.max - activeAxisLimitedBounds.min);
  const canPlayActiveAxisPreview =
    !!selectedBone &&
    selectedBoneIndex !== null &&
    !!onSetBoneEulerRotation &&
    activeAxisLimitedBounds !== null &&
    activeAxisLimitedRangeDegrees >= PREVIEW_MIN_RANGE_DEGREES;
  const canResetActiveAxisPreview =
    !!selectedBone &&
    selectedBoneIndex !== null &&
    !!onSetBoneEulerRotation;
  const isPreviewPlaying = previewPlaybackState === 'playing';
  const safeManipulatorScale = clampManipulatorScale(manipulatorScale);

  const resetToBaselinePose = useCallback(() => {
    if (
      !selectedBone ||
      selectedBoneIndex === null ||
      !onSetBoneEulerRotation
    ) {
      return;
    }
    const baseline = readBonePoseBaselineTransform(selectedBone).rotate;
    onSetBoneEulerRotation(selectedBoneIndex, [baseline[0], baseline[1], baseline[2]]);
  }, [
    onSetBoneEulerRotation,
    selectedBone,
    selectedBoneIndex,
  ]);

  const handleStartPreviewPlayback = useCallback(() => {
    if (
      !selectedBone ||
      selectedBoneIndex === null ||
      !activeAxisLimitedBounds ||
      !onSetBoneEulerRotation
    ) {
      return;
    }
    const currentEuler = readBonePoseTransform(selectedBone).rotate;
    const axisIndex = axisIndexFromKey(activeAxis);
    const currentAxisValue = currentEuler[axisIndex];
    const initialTarget = activeAxisLimitedBounds.max;
    const now = performance.now();
    clearPreviewAnimationFrame();
    previewEulerSnapshotRef.current = [currentEuler[0], currentEuler[1], currentEuler[2]];
    previewSegmentRef.current = {
      boneIndex: selectedBoneIndex,
      axis: activeAxis,
      min: activeAxisLimitedBounds.min,
      max: activeAxisLimitedBounds.max,
      from: currentAxisValue,
      to: initialTarget,
      startMs: now,
      durationMs: resolvePreviewDurationMs(currentAxisValue, initialTarget),
    };
    setPreviewPlaybackState('playing');
  }, [
    activeAxis,
    activeAxisLimitedBounds,
    clearPreviewAnimationFrame,
    onSetBoneEulerRotation,
    selectedBone,
    selectedBoneIndex,
  ]);

  const handlePausePreviewPlayback = useCallback(() => {
    stopPreviewPlayback('paused');
  }, [stopPreviewPlayback]);

  const handleStopPreviewPlayback = useCallback(() => {
    stopPreviewPlayback('stopped');
    resetToBaselinePose();
  }, [resetToBaselinePose, stopPreviewPlayback]);

  const anchorData = useMemo(() => {
    if (!selectedBone) return null;
    const position = new THREE.Vector3();
    const posedQuat = new THREE.Quaternion();
    selectedBone.posedWorld.decompose(position, posedQuat, new THREE.Vector3());

    const localMatrix = new THREE.Matrix4().multiplyMatrices(
      selectedBone.restLocal,
      selectedBone.poseLocal,
    );
    const { quaternion: localQuat } = decomposeMatrixToTransform(localMatrix);

    const gimbalQuats = buildGimbalQuaternions(
      posedQuat,
      localQuat,
      selectedBone.eulerOrder,
    );

    const restWorldQuat = new THREE.Quaternion();
    selectedBone.restWorld.decompose(
      new THREE.Vector3(),
      restWorldQuat,
      new THREE.Vector3(),
    );
    const boneLocalChildDir = selectedBone.direction
      .clone()
      .applyQuaternion(restWorldQuat.clone().invert());

    const baseline = readBonePoseBaselineTransform(selectedBone);
    const angleOffsets = computeGimbalAngleOffsets(
      boneLocalChildDir,
      baseline.rotate as [number, number, number],
      selectedBone.eulerOrder,
    );

    return { position, gimbalQuats, angleOffsets };
  }, [poseVersion, selectedBone]);

  const anchorPositionTuple = useMemo<[number, number, number] | null>(
    () =>
      anchorData
        ? [
            anchorData.position.x,
            anchorData.position.y,
            anchorData.position.z,
          ]
        : null,
    [anchorData],
  );

  const handleSetActiveAxisMode = (
    mode: 'unlimited' | 'limited' | 'locked',
  ) => {
    if (!selectedBone) return;
    const axisIndex = axisIndexFromKey(activeAxis);
    const limits = ensureRotationLimits(selectedBone);
    if (getRotationLimitsAxisMode(limits, axisIndex) === mode) return;
    setRotationLimitsAxisMode(limits, axisIndex, mode);
    pruneBonePropertiesDefaults(selectedBone);
    onBonePropertiesChange?.();
  };

  const handleHandlePointerDown = (
    axis: ManipulatorAxis,
    boundary: LimitBoundary,
    event: ThreeEvent<PointerEvent>,
  ) => {
    event.stopPropagation();
    if (event.nativeEvent.button !== 0) return;
    setActiveAxis(axis);

    if (!selectedBone) return;
    const limits = selectedBone.properties?.rotationLimits ?? new RotationLimits();
    const axisIndex = axisIndexFromKey(axis);
    const axisMode = getRotationLimitsAxisMode(limits, axisIndex);
    const dispBounds = resolveDisplayBounds(axisMode, limits.min[axisIndex], limits.max[axisIndex]);

    const initialMin = limits.min[axisIndex] ?? dispBounds.min;
    const initialMax = limits.max[axisIndex] ?? dispBounds.max;
    const initialValue = boundary === 'min' ? initialMin : initialMax;
    const otherValue = boundary === 'min' ? initialMax : initialMin;

    dragAnchorRef.current = {
      initialValue,
      otherBoundary: otherValue,
      lastAngle: normalizeDegrees(
        initialValue + (anchorData?.angleOffsets[axis] ?? 0),
      ),
      accumulatedDelta: 0,
      wasUnlimited: axisMode === 'unlimited',
    };
    setDragState({ axis, boundary });
  };

  useEffect(() => {
    if (previewPlaybackState !== 'playing') return;
    if (
      !selectedBone ||
      selectedBoneIndex === null ||
      !onSetBoneEulerRotation
    ) {
      stopPreviewPlayback('stopped');
      return;
    }

    const snapshot = previewEulerSnapshotRef.current;
    if (!snapshot) {
      stopPreviewPlayback('stopped');
      return;
    }

    const step = (timeMs: number) => {
      const segment = previewSegmentRef.current;
      const snap = previewEulerSnapshotRef.current;
      if (!segment || !snap) return;
      if (
        segment.boneIndex !== selectedBoneIndex ||
        segment.axis !== activeAxis
      ) {
        stopPreviewPlayback('stopped');
        return;
      }

      const axisIndex = axisIndexFromKey(segment.axis);
      const progress =
        segment.durationMs <= 0
          ? 1
          : THREE.MathUtils.clamp(
              (timeMs - segment.startMs) / segment.durationMs,
              0,
              1,
            );
      const easedProgress = easeInOutSine(progress);
      const targetAxisValue = THREE.MathUtils.lerp(
        segment.from,
        segment.to,
        easedProgress,
      );

      const nextEuler: [number, number, number] = [snap[0], snap[1], snap[2]];
      nextEuler[axisIndex] = targetAxisValue;
      onSetBoneEulerRotation(segment.boneIndex, nextEuler);

      if (progress >= 1) {
        const nextTarget =
          Math.abs(segment.to - segment.max) < PREVIEW_APPLY_EPSILON_DEGREES
            ? segment.min
            : segment.max;
        const nextStart = targetAxisValue;
        previewSegmentRef.current = {
          ...segment,
          from: nextStart,
          to: nextTarget,
          startMs: timeMs,
          durationMs: resolvePreviewDurationMs(nextStart, nextTarget),
        };
      }

      previewRafIdRef.current = window.requestAnimationFrame(step);
    };

    previewRafIdRef.current = window.requestAnimationFrame(step);
    return () => {
      clearPreviewAnimationFrame();
    };
  }, [
    activeAxis,
    clearPreviewAnimationFrame,
    onSetBoneEulerRotation,
    previewPlaybackState,
    selectedBone,
    selectedBoneIndex,
    stopPreviewPlayback,
  ]);

  useEffect(() => {
    if (!dragState) return;

    onDraggingChange?.(true);
    if (cameraControls) cameraControls.enabled = false;

    const handlePointerMove = (event: PointerEvent) => {
      if (!selectedBone || !enabled) return;
      const anchor = dragAnchorRef.current;
      if (!anchor) return;

      const rect = gl.domElement.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      ndcRef.current.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      );

      selectedBone.posedWorld.decompose(
        anchorPositionRef.current,
        anchorQuaternionRef.current,
        anchorScaleRef.current,
      );

      const localMatrix = new THREE.Matrix4().multiplyMatrices(
        selectedBone.restLocal,
        selectedBone.poseLocal,
      );
      const { quaternion: dragLocalQuat } = decomposeMatrixToTransform(localMatrix);

      const dragGimbalQuats = buildGimbalQuaternions(
        anchorQuaternionRef.current,
        dragLocalQuat,
        selectedBone.eulerOrder,
      );
      anchorQuaternionRef.current.copy(dragGimbalQuats[dragState.axis]);

      axisNormalRef.current
        .copy(axisNormalVector(dragState.axis))
        .applyQuaternion(anchorQuaternionRef.current)
        .normalize();
      planeRef.current.setFromNormalAndCoplanarPoint(
        axisNormalRef.current,
        anchorPositionRef.current,
      );

      raycasterRef.current.setFromCamera(ndcRef.current, camera);
      const intersection = raycasterRef.current.ray.intersectPlane(
        planeRef.current,
        intersectionRef.current,
      );
      if (!intersection) return;

      localPointRef.current
        .copy(intersection)
        .sub(anchorPositionRef.current)
        .applyQuaternion(anchorQuaternionRef.current.clone().invert());

      const rawAngle = angleDegreesFromLocalPoint(
        dragState.axis,
        localPointRef.current,
      );

      let frameDelta = rawAngle - anchor.lastAngle;
      while (frameDelta > 180) frameDelta -= 360;
      while (frameDelta < -180) frameDelta += 360;
      anchor.lastAngle = rawAngle;
      anchor.accumulatedDelta += frameDelta;

      const rawNewValue = anchor.initialValue + anchor.accumulatedDelta;
      let clampedValue: number;
      if (dragState.boundary === 'min') {
        clampedValue = THREE.MathUtils.clamp(
          rawNewValue,
          anchor.otherBoundary - 360,
          anchor.otherBoundary - MIN_LIMIT_RANGE_DEGREES,
        );
      } else {
        clampedValue = THREE.MathUtils.clamp(
          rawNewValue,
          anchor.otherBoundary + MIN_LIMIT_RANGE_DEGREES,
          anchor.otherBoundary + 360,
        );
      }

      const limits = ensureRotationLimits(selectedBone);
      const axisIndex = axisIndexFromKey(dragState.axis);
      const axisMode = getRotationLimitsAxisMode(limits, axisIndex);
      if (axisMode === 'locked') return;

      if (anchor.wasUnlimited) {
        setRotationLimitsAxisMode(limits, axisIndex, 'limited');
        if (dragState.boundary === 'min') {
          limits.min[axisIndex] = clampedValue;
          limits.max[axisIndex] = anchor.otherBoundary;
        } else {
          limits.min[axisIndex] = anchor.otherBoundary;
          limits.max[axisIndex] = clampedValue;
        }
        anchor.wasUnlimited = false;
      } else {
        const currentValue =
          dragState.boundary === 'min'
            ? limits.min[axisIndex]
            : limits.max[axisIndex];
        if (currentValue === clampedValue) return;

        if (dragState.boundary === 'min') {
          limits.min[axisIndex] = clampedValue;
        } else {
          limits.max[axisIndex] = clampedValue;
        }
      }

      pruneBonePropertiesDefaults(selectedBone);
      onBonePropertiesChange?.();
    };

    const endDrag = () => {
      setDragState(null);
      dragAnchorRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      onDraggingChange?.(false);
      if (cameraControls) cameraControls.enabled = true;
    };
  }, [
    camera,
    cameraControls,
    dragState,
    enabled,
    gl,
    onBonePropertiesChange,
    onDraggingChange,
    selectedBone,
  ]);

  useEffect(() => {
    const segment = previewSegmentRef.current;
    if (!segment) return;
    if (segment.axis !== activeAxis) {
      stopPreviewPlayback('stopped');
    }
  }, [activeAxis, stopPreviewPlayback]);

  useEffect(() => {
    if (previewPlaybackState === 'stopped') return;
    if (activeAxisMode !== 'limited' || dragState !== null) {
      stopPreviewPlayback('stopped');
    }
  }, [activeAxisMode, dragState, previewPlaybackState, stopPreviewPlayback]);

  useEffect(() => {
    if (enabled && selectedBone) return;
    stopPreviewPlayback('stopped');
    setDragState(null);
    dragAnchorRef.current = null;
  }, [enabled, selectedBone, stopPreviewPlayback]);

  useEffect(
    () => () => {
      clearPreviewAnimationFrame();
      previewSegmentRef.current = null;
      previewEulerSnapshotRef.current = null;
    },
    [clearPreviewAnimationFrame],
  );

  useFrame(() => {
    if (!enabled || !anchorData) return;
    const scale =
      computeTransformControlsScale(camera, anchorData.position) *
      safeManipulatorScale;

    const ms = manipulatorStateRef.current;
    ms.position.copy(anchorData.position);
    ms.quaternion.identity();
    ms.scale = scale;

    const root = rootRef.current;
    if (root) {
      root.scale.setScalar(scale);
    }

    const lockPillAnchor = lockPillAnchorRef.current;
    if (!lockPillAnchor) return;

    const radiusDistance = scale * RING_RADIUS * LOCK_PILL_TOP_RIGHT_FACTOR;
    cameraRightRef.current.setFromMatrixColumn(camera.matrixWorld, 0).normalize();
    cameraUpRef.current.setFromMatrixColumn(camera.matrixWorld, 1).normalize();
    cameraDiagonalRef.current
      .copy(cameraRightRef.current)
      .add(cameraUpRef.current);
    if (cameraDiagonalRef.current.lengthSq() <= 1e-10) {
      cameraDiagonalRef.current.copy(cameraUpRef.current);
    } else {
      cameraDiagonalRef.current.normalize();
    }

    lockPillWorldPositionRef.current
      .copy(anchorData.position)
      .addScaledVector(cameraDiagonalRef.current, radiusDistance);
    lockPillAnchor.position.copy(lockPillWorldPositionRef.current);
  });

  if (!enabled || !selectedBone || !anchorPositionTuple || !anchorData) {
    return null;
  }

  return (
    <>
      <group
        ref={rootRef}
        position={anchorPositionTuple}
      >
        {axisStates.map((axisState) => (
          <RotationLimitsAxis
            key={axisState.axis}
            axisState={axisState}
            isActive={axisState.axis === activeAxis}
            axisQuaternion={anchorData.gimbalQuats[axisState.axis]}
            angleOffset={anchorData.angleOffsets[axisState.axis]}
            manipulatorStateRef={manipulatorStateRef}
            onSelectAxis={setActiveAxis}
            onHandlePointerDown={handleHandlePointerDown}
          />
        ))}
      </group>

      <group ref={lockPillAnchorRef}>
        <Html position={[0, 0, 0]} zIndexRange={[35, 20]} style={{ pointerEvents: 'auto' }}>
          <div
            className="pointer-events-auto"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="inline-flex items-center gap-1">
              <div
                className="inline-flex h-8 items-center gap-1 rounded-full border border-white/60 px-2 backdrop-blur-sm"
                style={{ backgroundColor: activeAxisColorCss, opacity: 0.94 }}
              >
                <Text
                  as="span"
                  variant="studio/body/medium/xs"
                  className="mr-0.5 block tabular-nums text-white uppercase"
                >
                  {activeAxisLabel}
                </Text>
                <button
                  type="button"
                  onClick={() => handleSetActiveAxisMode('unlimited')}
                  aria-label="Free rotation"
                  className={`inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors ${
                    activeAxisMode === 'unlimited'
                      ? 'bg-white/95'
                      : 'bg-white/20 hover:bg-white/40'
                  }`}
                >
                  <svg
                    viewBox="0 0 12 12"
                    className={`h-3 w-3 ${activeAxisMode === 'unlimited' ? 'text-gray-90' : 'text-white/80'}`}
                  >
                    <circle
                      cx="6"
                      cy="6"
                      r="4.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => handleSetActiveAxisMode('limited')}
                  aria-label="Limited rotation"
                  className={`inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors ${
                    activeAxisMode === 'limited'
                      ? 'bg-white/95'
                      : 'bg-white/20 hover:bg-white/40'
                  }`}
                >
                  <svg
                    viewBox="0 0 12 12"
                    className={`h-3 w-3 ${activeAxisMode === 'limited' ? 'text-gray-90' : 'text-white/80'}`}
                  >
                    <path
                      d="M6 6 L6 1 A5 5 0 1 1 1 6 Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => handleSetActiveAxisMode('locked')}
                  aria-label="Lock rotation"
                  className={`inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors ${
                    activeAxisMode === 'locked'
                      ? 'bg-white/95'
                      : 'bg-white/20 hover:bg-white/40'
                  }`}
                >
                  <LockIcon
                    className={`h-3 w-3 ${activeAxisMode === 'locked' ? 'text-gray-90' : 'text-white/80'}`}
                  />
                </button>
              </div>
              {activeAxisMode === 'limited' && (
                <div
                  className="inline-flex h-8 items-center gap-1 rounded-full border border-white/60 px-1.5 backdrop-blur-sm"
                  style={{ backgroundColor: activeAxisColorCss, opacity: 0.94 }}
                >
                  <button
                    type="button"
                    onClick={handleStopPreviewPlayback}
                    aria-label="Stop rotation preview"
                    disabled={!canResetActiveAxisPreview}
                    className={`inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors ${
                      canResetActiveAxisPreview
                        ? 'bg-white/20 text-white/80 hover:bg-white/40'
                        : 'cursor-not-allowed bg-white/10 text-white/40 hover:bg-white/10'
                    }`}
                  >
                    <svg viewBox="0 0 12 12" className="h-3 w-3">
                      <rect x="2" y="2" width="8" height="8" fill="currentColor" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={
                      isPreviewPlaying
                        ? handlePausePreviewPlayback
                        : handleStartPreviewPlayback
                    }
                    aria-label={
                      isPreviewPlaying
                        ? 'Pause rotation preview'
                        : 'Play rotation preview'
                    }
                    disabled={!isPreviewPlaying && !canPlayActiveAxisPreview}
                    className={`inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors ${
                      isPreviewPlaying
                        ? 'bg-white/95 text-gray-90'
                        : 'bg-white/20 text-white/80 hover:bg-white/40'
                    } ${
                      !isPreviewPlaying && !canPlayActiveAxisPreview
                        ? 'cursor-not-allowed bg-white/10 text-white/40 hover:bg-white/10'
                        : ''
                    }`}
                  >
                    {isPreviewPlaying ? (
                      <PauseIcon className="h-3 w-3" />
                    ) : (
                      <PlayIcon className="h-3 w-3" />
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </Html>
      </group>
    </>
  );
}
